import { create } from "zustand";
import { api, getToken, setToken } from "@/lib/api/client";
import { signInWithGoogle } from "@/lib/auth/firebase";
import { PERMISSION_ALL, type Permission } from "@/lib/permissions/catalog";

export type NexusUser = {
  id: number;
  email: string;
  name: string;
  avatarUrl?: string | null;
  roles: { slug: string; name: string }[];
  permissions: string[]; // unión de permisos de todos sus roles; puede incluir "*"
};

type SessionState = {
  user: NexusUser | null;
  loading: boolean;
  error: string | null;
  hydrate: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => void;
  hasPermission: (perm: Permission | string) => boolean;
};

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  loading: true,
  error: null,

  hydrate: async () => {
    const token = getToken();
    if (!token) {
      set({ loading: false, user: null });
      return;
    }
    try {
      const { data } = await api.get<NexusUser>("/api/nexus/me");
      set({ user: data, loading: false, error: null });
    } catch {
      setToken(null);
      set({ user: null, loading: false });
    }
  },

  /**
   * Login via Google + Firebase Auth, idéntico al flow de MNW-User.
   *
   * Flow:
   *   1. signInWithPopup → Firebase id token
   *   2. POST /users/google-authentication { token, portal: "nexus" }
   *      - backend valida allowlist (NEXUS_ALLOWED_EMAILS)
   *      - devuelve JWT propio de Mixcore + datos del User
   *   3. Guardamos el JWT como nx_token y hidratamos /api/nexus/me
   *      (que devuelve roles + permisos efectivos del user).
   *
   * El backend (`requirePermission`) ya entiende este JWT — usa el `id`
   * del payload para resolver los permisos via NexusUserRole.
   */
  loginWithGoogle: async () => {
    set({ loading: true, error: null });
    try {
      const { idToken } = await signInWithGoogle();

      const { data } = await api.post<{ token: string; usuario: unknown }>(
        "/users/google-authentication",
        { token: idToken, portal: "nexus" },
      );

      if (!data?.token) {
        throw new Error("El backend no devolvió token.");
      }

      setToken(data.token);
      await get().hydrate();
    } catch (err: any) {
      // Firebase popup errors: auth/popup-closed-by-user, auth/popup-blocked.
      // Backend allowlist: 403 con { error: "Acceso denegado al portal Nexus" }.
      // Backend sin rol Nexus asignado: 403 desde /api/nexus/me con
      // "No tienes acceso a Nexus...".
      const firebaseCode = err?.code as string | undefined;
      let msg = "Error al iniciar sesión.";

      if (firebaseCode === "auth/popup-closed-by-user") {
        msg = "Cerraste el popup antes de completar el login.";
      } else if (firebaseCode === "auth/popup-blocked") {
        msg = "Tu navegador bloqueó el popup. Habilitá popups para nexus.mixcore.club.";
      } else if (err?.response?.status === 403) {
        msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "No tienes acceso autorizado al portal Nexus.";
      } else if (err?.response?.data?.error) {
        msg = err.response.data.error;
      } else if (err?.message) {
        msg = err.message;
      }

      setToken(null);
      set({ loading: false, error: msg, user: null });
      throw err;
    }
  },

  logout: () => {
    setToken(null);
    set({ user: null });
  },

  hasPermission: (perm) => {
    const u = get().user;
    if (!u) return false;
    if (u.permissions.includes(PERMISSION_ALL)) return true;
    return u.permissions.includes(perm);
  },
}));

// Listeners globales para 401 emitidos por el axios interceptor.
if (typeof window !== "undefined") {
  window.addEventListener("nx:auth:expired", () => {
    useSession.getState().logout();
  });
}
